import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/* Content lives in public/objections/objections.json so Joy can reword a
   response without anyone touching code. */
const SRC = '/objections/objections.json';

function embedUrl(url) {
  if (!url) return null;
  let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  m = url.match(/drive\.google\.com\/file\/d\/([^/]+)/) || url.match(/drive\.google\.com\/open\?id=([\w-]+)/);
  if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
  return null;
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Montserrat:wght@500;600;700;800&display=swap');
.ob{--ink:#0f1724;--dim:#686e77;--line:#e2e5e9;--gold:#C9922A;--green:#0d1f3b;--red:#B4453A;--paper:#fff;
  font-family:Montserrat,system-ui,sans-serif;color:var(--ink);max-width:860px;padding-bottom:56px}
.ob-eb{font-size:10.5px;font-weight:800;letter-spacing:.2em;text-transform:uppercase;color:var(--gold);margin-bottom:7px}
.ob-h{font-family:Fraunces,Georgia,serif;font-size:clamp(25px,3.3vw,33px);font-weight:700;margin:0;letter-spacing:-.02em;line-height:1.06}
.ob-l{color:var(--dim);font-size:13.5px;margin-top:8px;max-width:60ch;line-height:1.55}
.ob-back{background:none;border:none;font:inherit;font-size:13px;font-weight:700;color:var(--dim);cursor:pointer;
  padding:0;margin-bottom:14px;display:inline-flex;gap:7px;align-items:center}
.ob-back:hover{color:var(--ink)}
.ob-group{font-family:Fraunces,Georgia,serif;font-size:18px;font-weight:700;margin:28px 0 3px}
.ob-gsub{font-size:12.5px;color:var(--dim);margin-bottom:13px}
.ob-list{display:grid;gap:10px}
.ob-item{display:flex;gap:14px;align-items:flex-start;width:100%;text-align:left;font:inherit;cursor:pointer;
  background:var(--paper);border:1px solid var(--line);border-left:4px solid var(--red);border-radius:15px;
  padding:16px 18px;transition:transform .22s cubic-bezier(.2,.8,.3,1),box-shadow .22s,border-color .22s}
.ob-item:hover{transform:translateY(-2px);box-shadow:0 12px 26px -16px rgba(18,40,31,.4);border-color:#cbd0d8;border-left-color:var(--red)}
.ob-item:focus-visible{outline:3px solid var(--gold);outline-offset:3px}
.ob-quote{flex:1;min-width:0}
.ob-says{font-size:15.5px;font-weight:700;line-height:1.32}
.ob-hint{font-size:12.5px;color:var(--dim);margin-top:4px;line-height:1.45}
.ob-arrow{color:#b9bec6;font-size:19px;transition:transform .22s,color .22s}
.ob-item:hover .ob-arrow{transform:translateX(4px);color:var(--green)}

.ob-hero{background:linear-gradient(135deg,#8E3529,#6E2418);border-radius:20px;padding:24px 26px;color:#fff;margin-bottom:20px}
.ob-hero .k{font-size:10px;font-weight:800;letter-spacing:.16em;opacity:.75;margin-bottom:8px}
.ob-hero .q{font-family:Fraunces,Georgia,serif;font-size:clamp(21px,3vw,28px);font-weight:700;line-height:1.18;letter-spacing:-.015em}
.ob-card{background:var(--paper);border:1px solid var(--line);border-radius:18px;padding:20px 22px;margin-bottom:14px}
.ob-card h3{font-family:Fraunces,Georgia,serif;font-size:16.5px;margin:0 0 9px;font-weight:700}
.ob-card p{margin:0;font-size:14px;line-height:1.62;color:#373d47}
.ob-say{list-style:none;margin:0;padding:0}
.ob-say li{display:flex;gap:11px;align-items:flex-start;padding:9px 0;border-bottom:1px solid #f2f3f5;font-size:14px;line-height:1.55}
.ob-say li:last-child{border-bottom:none}
.ob-say li b{color:var(--green);flex:0 0 auto;font-weight:800}
.ob-warn{background:#FFF8E6;border:1px solid #F0D99A;border-radius:14px;padding:14px 17px;font-size:13.5px;
  color:#6B4E10;line-height:1.55;display:flex;gap:11px}
.ob-vid{margin-top:14px;border-radius:14px;overflow:hidden;background:#000;aspect-ratio:16/9}
.ob-vid iframe{width:100%;height:100%;border:0}
.ob-novid{border:1px dashed #d5d8de;border-radius:14px;padding:18px;text-align:center;color:var(--dim);font-size:12.5px}
.ob-empty{border:1px dashed #d5d8de;border-radius:16px;padding:34px;text-align:center;color:var(--dim);font-size:13.5px}
@media(max-width:560px){.ob-item{padding:14px 15px}}
`;

export default function ObjectionRoom() {
  const [params, setParams] = useSearchParams();
  const slug = params.get('o');
  const [items, setItems] = useState(null);

  useEffect(() => {
    fetch(SRC).then(r => r.ok ? r.json() : Promise.reject()).then(setItems).catch(() => setItems([]));
  }, []);

  const open = (s) => {
    const n = new URLSearchParams(params);
    s ? n.set('o', s) : n.delete('o');
    setParams(n); window.scrollTo(0, 0);
  };
  const item = items?.find(o => o.slug === slug) || null;
  const groups = items ? [...new Set(items.map(o => o.when))] : [];

  return (
    <>
      <style>{CSS}</style>
      <div className="page-header"><h2>🛡️ Objection Room</h2></div>

      <div className="page-body ob">
        {!items ? <div style={{ padding: 26, color: '#aaa' }}>Loading…</div>
          : !item ? (<>
            <div className="ob-eb">Amanda's University</div>
            <h1 className="ob-h">What do I say when they say…</h1>
            <div className="ob-l">
              Every one of these is a reflex, not a decision. Ninety percent of sales start with
              resistance — the objection means the sale is still alive. Pick what you are hearing.
            </div>
            {items.length === 0 && <div className="ob-empty" style={{ marginTop: 22 }}>No objections added yet.</div>}
            {groups.map(g => (
              <div key={g}>
                <div className="ob-group">{g}</div>
                <div className="ob-gsub">
                  {items.filter(o => o.when === g).length} objection{items.filter(o => o.when === g).length === 1 ? '' : 's'}
                </div>
                <div className="ob-list">
                  {items.filter(o => o.when === g).map(o => (
                    <button key={o.slug} className="ob-item" onClick={() => open(o.slug)}>
                      <span className="ob-quote">
                        <span className="ob-says" style={{ display: 'block' }}>“{o.says}”</span>
                        <span className="ob-hint" style={{ display: 'block' }}>{o.means}</span>
                      </span>
                      <span className="ob-arrow" aria-hidden>›</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </>) : (<>
            <button className="ob-back" onClick={() => open(null)}>← All objections</button>
            <div className="ob-hero">
              <div className="k">{item.when.toUpperCase()} · THEY SAY</div>
              <div className="q">“{item.says}”</div>
            </div>

            <div className="ob-card">
              <h3>What they actually mean</h3>
              <p>{item.means}</p>
            </div>

            <div className="ob-card">
              <h3>What to say</h3>
              <ul className="ob-say">
                {item.say.map((line, i) => (
                  <li key={i}><b>{i + 1}</b><span>{line}</span></li>
                ))}
              </ul>
            </div>

            {item.watch && (
              <div className="ob-warn"><span aria-hidden>⚠️</span><span>{item.watch}</span></div>
            )}

            {item.video && embedUrl(item.video) ? (
              <div className="ob-vid">
                <iframe title={item.says} src={embedUrl(item.video)} allow="encrypted-media; fullscreen" allowFullScreen />
              </div>
            ) : (
              <div className="ob-novid" style={{ marginTop: 14 }}>No video on this one yet.</div>
            )}
          </>)}
      </div>
    </>
  );
}
